import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { useContractList } from "../hooks/use-contracts";
import { formatDate } from "../lib/date";
import { apiUrl } from "../lib/api";
import styles from "./Contracts.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/contracts",
	component: withPermission(ContractsPage, "contracts", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
	}),
});

function ContractsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const searchParams = route.useSearch();
	const status = searchParams.status;
	const page = searchParams.page;
	const pageSize = searchParams.size;
	const [type, setType] = useState<string>("");
	const searchQuery = searchParams.search;
	const createdAtFrom = searchParams.createdAtFrom;
	const createdAtTo = searchParams.createdAtTo;
	const { data, isLoading, isError, refetch } = useContractList({
		status: status || undefined,
		type: type || undefined,
		search: searchQuery || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		page,
		limit: pageSize,
	});

	const contracts = data?.items ?? [];
	const total = data?.total ?? 0;

	const statusTabs = [
		{ key: "", label: t("all") },
		{ key: "draft", label: t("draft") },
		{ key: "active", label: t("active") },
		{ key: "expired", label: t("expired") },
		{ key: "terminated", label: t("terminated") },
		{ key: "renewed", label: t("renewed") },
	];

	return (
		<div>
			<PageHeader
				title={t("contracts")}
				action={
					<Link to="/contracts/new" className={styles.newLink}>
						<Button>{t("newContract")}</Button>
					</Link>
				}
			/>

			<div className={styles.filters}>
				<div className={styles.tabs}>
					{statusTabs.map((tab) => (
						<button
							type="button"
							key={tab.key}
							className={[styles.tab, status === tab.key && styles.active]
								.filter(Boolean)
								.join(" ")}
							onClick={() => {
								navigate({ to: ".", search: { status: tab.key, page: 1, size: pageSize } });
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
				<div className={styles.row}>
					<FilterBar
						filters={[
							{
								key: "type",
								type: "select",
								label: t("all"),
								options: [
									{ value: "sales", label: t("sales") },
									{ value: "maintenance", label: t("maintenance") },
									{ value: "rental", label: t("rental") },
									{ value: "service", label: t("service") },
								],
							},
							{ key: "search", type: "text", label: t("search") },
							{ key: "createdAt", type: "dateRange", label: t("createdAt") },
						]}
						values={{ type, search: searchQuery, createdAtFrom, createdAtTo }}
						onChange={(vals) => {
							setType(vals.type || "");
							navigate({
								to: ".",
								search: {
									status,
									search: vals.search || "",
									createdAtFrom: vals.createdAtFrom || "",
									createdAtTo: vals.createdAtTo || "",
									page: 1,
									size: pageSize,
								},
							});
						}}
					/>
				</div>
			</div>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						columns={[
							{
								key: "documentNumber",
								header: t("documentNumber"),
								render: (row) => row.documentNumber || row.id.slice(0, 8),
							},
							{
								key: "title",
								header: t("title"),
								render: (row) => row.title,
							},
							{
								key: "company",
								header: t("company"),
								render: (row) => row.companyName || row.companyId || "-",
							},
							{
								key: "type",
								header: t("type"),
								render: (row) => t(row.type),
							},
							{
								key: "startDate",
								header: t("startDate"),
								render: (row) => (row.startDate ? formatDate(row.startDate) : "-"),
							},
							{
								key: "endDate",
								header: t("endDate"),
								render: (row) => (row.endDate ? formatDate(row.endDate) : "-"),
							},
							{
								key: "status",
								header: t("status"),
								render: (row) => <StatusBadge status={row.status} type="contract" />,
							},
							{
								key: "value",
								header: t("value"),
								render: (row) => `${row.value || "-"} ${row.currency || ""}`,
							},
							{
								key: "downloadForm",
								header: t("downloadForm"),
								render: (row) => (
									<div className={styles.formLinkRow}>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(apiUrl(`/api/contracts/${row.id}/pdf?lang=en`), "_blank")
											}
										>
											EN
										</button>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(apiUrl(`/api/contracts/${row.id}/pdf?lang=tr`), "_blank")
											}
										>
											TR
										</button>
									</div>
								),
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/contracts/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={contracts}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(newPage) =>
						navigate({ to: ".", search: { status, page: newPage, size: pageSize } })
					}
					onPageSizeChange={(newSize) =>
						navigate({ to: ".", search: { status, page: 1, size: newSize } })
					}
				/>
			</Card>
		</div>
	);
}
